import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Button,
  Tabs,
  Tab,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import PlayArrow from '@mui/icons-material/PlayArrow';
import Phone from '@mui/icons-material/Phone';
import Favorite from '@mui/icons-material/Favorite';
import GroupsOutlined from '@mui/icons-material/GroupsOutlined';
import VolunteerActivismOutlined from '@mui/icons-material/VolunteerActivismOutlined';
import CardGiftcardOutlined from '@mui/icons-material/CardGiftcardOutlined';
import PublicOutlined from '@mui/icons-material/PublicOutlined';
import FormatQuote from '@mui/icons-material/FormatQuote';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import ArrowForward from '@mui/icons-material/ArrowForward';
import { ASSETS } from '../utils/assets';
import { campaignRatingService } from '../services/api';
import DonateNowButton from '../components/DonateNowButton';
import SimplePageBanner from '../components/SimplePageBanner';
import SiteStatsItems from '../components/SiteStatsItems';

const heartClipPath = 'polygon(50% 0%, 100% 35%, 100% 65%, 50% 100%, 0% 65%, 0% 35%)';
/** Hexagon (flat top) for About section main image */
const hexagonClipPath = 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)';

const DARK_CHARCOAL = '#2D2F2D';
const DARK_SECTION = '#1A2020';
const BRAND_GREEN = '#477C2E';
const BRAND_ORANGE = '#E85D1C';
const handPattern = `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 5 L35 20 L30 18 L25 20 Z' fill='none' stroke='rgba(255,255,255,0.06)' stroke-width='1'/%3E%3C/svg%3E")`;

const AboutPage = () => {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [reviewIndex, setReviewIndex] = useState(0);
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    campaignRatingService
      .getPublic()
      .then((res) => {
        const payload = res?.data?.data ?? res?.data ?? [];
        const items = Array.isArray(payload) ? payload : [];
        const mapped = items
          .map((row) => ({
            id: row.id,
            text: String(row.review || '').trim(),
            author: row.full_name || 'Supporter',
            rating: Number(row.rating) || 0,
          }))
          .filter((row) => row.text)
          .slice(0, 10);
        setReviews(mapped);
        setReviewIndex(0);
      })
      .catch(() => {});
  }, []);

  const missionVisionHistory = [
    {
      tab: 'Our Mission',
      image: ASSETS.missionImage,
      heading: 'Where can I get some?',
      paragraph: 'To create sustainable social impact through community engagement, education, and empowerment. We strive to address root causes of poverty, inequality, and environmental challenges.',
      bullets: [
        'We strive to address root causes of poverty and inequality.',
        'Building resilient and inclusive communities worldwide.',
        'Education, healthcare, environment, and livelihoods.',
      ],
      secondParagraph: 'Our mission drives every program and partnership we undertake.',
    },
    {
      tab: 'Our Vision',
      image: ASSETS.cause1,
      heading: 'A world of opportunity',
      paragraph: 'A world where every individual has the opportunity to reach their full potential, where communities thrive sustainably, and where social justice and environmental stewardship are the foundation.',
      bullets: [
        'Every individual reaching their full potential.',
        'Communities thriving sustainably.',
        'Social justice and environmental stewardship as foundations.',
      ],
      secondParagraph: 'We work daily to bring this vision closer to reality.',
    },
    {
      tab: 'Our History',
      image: ASSETS.historyImage,
      heading: 'From grassroots to growth',
      paragraph: 'Roots & Wings was founded with a vision to create meaningful change in communities facing social and economic challenges. What began as a small grassroots initiative has grown into a dynamic organization.',
      bullets: [
        'Started as a small grassroots initiative.',
        'Now working across education, healthcare, environment, and livelihoods.',
        'Committed to long-term, measurable impact.',
      ],
      secondParagraph: 'Our history is the foundation for our future impact.',
    },
  ];

  const cardContent = missionVisionHistory[tabValue];

  return (
    <Box sx={{ overflow: 'hidden' }}>
      <SimplePageBanner
        subtitle="Who we are"
        title="About Us"
        description="Learn about our mission, vision, and the communities we serve with compassion and purpose."
      />

      {/* Helping Each Other - hexagon image, badge, About US, Start Helping Team, Explore More, Call Any Time */}
      <Box
        sx={{
          position: 'relative',
          backgroundColor: '#fff',
          py: { xs: 6, md: 10 },
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: '30%',
            maxWidth: 280,
            background: 'linear-gradient(135deg, rgba(232,93,28,0.08) 0%, transparent 70%)',
            pointerEvents: 'none',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            right: 0,
            bottom: 0,
            width: 180,
            height: 180,
            background: `radial-gradient(circle, rgba(232,93,28,0.06) 0%, transparent 70%)`,
            borderRadius: '50%',
            pointerEvents: 'none',
          },
        }}
      >
        <Container maxWidth={false} sx={{ width: '100%', px: { xs: 2, sm: 3 }, position: 'relative', zIndex: 1 }}>
          <Grid container alignItems="center" spacing={{ xs: 0, md: 2 }}>
            <Grid item xs={12} md={6}>
              <Box sx={{ position: 'relative', display: 'flex', mb: { xs: 4, md: 0 }, justifyContent: 'center' }}>
                <Box sx={{ position: 'relative', width: { xs: 280, sm: 340, md: 400 }, height: { xs: 280, sm: 340, md: 400 } }}>
                  <Box
                    component="img"
                    src={ASSETS.parallax}
                    alt="People helping"
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      clipPath: hexagonClipPath,
                      border: '8px solid #F9F4E8',
                      boxSizing: 'border-box',
                    }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 16,
                      right: 16,
                      width: 72,
                      height: 72,
                      borderRadius: '50%',
                      backgroundColor: BRAND_ORANGE,
                      color: '#fff',
                      fontFamily: '"Poppins", sans-serif',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      textAlign: 'center',
                      lineHeight: 1.15,
                      px: 0.5,
                    }}
                  >
                    <Typography component="span" sx={{ fontWeight: 800, fontSize: { xs: 18, md: 20 }, display: 'block' }}>15+</Typography>
                    <Typography component="span" sx={{ fontWeight: 500, fontSize: { xs: 8, md: 9 }, display: 'block' }}>Years Of Experience</Typography>
                  </Box>
                  <Box sx={{ position: 'absolute', left: { xs: -8, md: -20 }, bottom: '30%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Box
                      sx={{
                        width: 72,
                        height: 72,
                        borderRadius: '50%',
                        border: '4px solid #fff',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        overflow: 'hidden',
                        backgroundColor: BRAND_ORANGE,
                      }}
                    >
                      <Box component="img" src={ASSETS.image08} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </Box>
                    <Box sx={{ width: 0, height: 0, borderLeft: '14px solid transparent', borderRight: '14px solid transparent', borderTop: `20px solid ${BRAND_ORANGE}`, mt: -0.5, filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))' }} />
                  </Box>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                <Favorite sx={{ fontSize: 18, color: BRAND_ORANGE }} />
                <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontWeight: 600, fontSize: 14, color: BRAND_ORANGE, letterSpacing: '0.02em' }}>
                  About US
                </Typography>
              </Box>
              <Typography
                component="h2"
                sx={{ fontFamily: '"Poppins", sans-serif', fontWeight: 700, fontSize: { xs: 26, md: 36 }, lineHeight: 1.25, color: '#2D2929', mb: 2, maxWidth: 540 }}
              >
                Helping Each Other can Make World Better
              </Typography>
              <Typography
                sx={{
                  fontFamily: '"Poppins", sans-serif',
                  fontWeight: 400,
                  fontSize: 14,
                  lineHeight: 1.8,
                  color: '#4B4646',
                  mb: 3,
                  maxWidth: 575,
                }}
              >
                Roots & Wings is committed to creating positive social impact through community engagement, education, and sustainable development initiatives. We work towards empowering individuals and communities to achieve their full potential.
              </Typography>
              <Box sx={{ mb: 3, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'center', gap: { xs: 2, md: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flex: { xs: 'none', md: '0 0 auto' }, minWidth: 0 }}>
                  <Box sx={{ width: 48, height: 48, borderRadius: '50%', backgroundColor: BRAND_ORANGE, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <VolunteerActivismOutlined sx={{ fontSize: 24 }} />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontWeight: 700, fontSize: 17, color: '#2D2929', mb: 0.5 }}>
                      Start Helping Team
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: BRAND_ORANGE, mt: 1, flexShrink: 0 }} />
                      <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontWeight: 400, fontSize: 13, color: '#6b7280', lineHeight: 1.5 }}>
                        There are many variations of solve
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', ml: { md: 1 } }}>
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Box
                      key={i}
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: '50%',
                        border: '2px solid #fff',
                        overflow: 'hidden',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                        ml: i > 1 ? -2 : 0,
                        boxSizing: 'content-box',
                        backgroundColor: i === 1 ? '#c4a574' : '#ddd',
                      }}
                    >
                      <Box component="img" src={ASSETS.parallax} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                <Button
                  variant="contained"
                  disableElevation
                  startIcon={
                    <Box sx={{ width: 32, height: 32, borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <PlayArrow sx={{ fontSize: 20, color: '#fff' }} />
                    </Box>
                  }
                  onClick={() => navigate('/campaigns')}
                  sx={{
                    height: 50,
                    borderRadius: 9999,
                    backgroundColor: BRAND_ORANGE,
                    color: '#FFFFFF',
                    fontFamily: '"Poppins", sans-serif',
                    fontWeight: 600,
                    fontSize: 15,
                    px: 3,
                    '&:hover': { backgroundColor: '#d14e14' },
                    '& .MuiButton-startIcon': { mr: 1.5 },
                  }}
                >
                  EXPLORE MORE
                </Button>
                <Box
                  component="a"
                  href="tel:9420249494"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    textDecoration: 'none',
                    color: 'inherit',
                  }}
                >
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      border: '2px solid #9ca3af',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Phone sx={{ fontSize: 22, color: '#4B4646', transform: 'rotate(-15deg)' }} />
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'center' }}>
                    <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontWeight: 400, fontSize: 12, color: '#6b7280', lineHeight: 1.3 }}>Call Any Time</Typography>
                    <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontWeight: 700, fontSize: 15, color: '#2D2929', lineHeight: 1.3 }}>+61 2541-528 950</Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Statistics Counter - same width as Charities section (1000px), thin outline circles, icon + number + label */}
      <Box sx={{ backgroundColor: DARK_SECTION, py: { xs: 6, md: 8 } }}>
        <Container maxWidth="lg" sx={{ width: '100%', px: { xs: 2, sm: 3 } }}>
          <Box sx={{ maxWidth: 1000, width: '100%', mx: 'auto' }}>
            <Grid container spacing={4} justifyContent="space-around" alignItems="stretch">
              {[
                { value: '260+', label: 'Total Happy Children', Icon: GroupsOutlined },
                { value: '110+', label: 'Total Our Volunteer', Icon: VolunteerActivismOutlined },
                { value: '190+', label: 'Our Products & Gifts', Icon: CardGiftcardOutlined },
                { value: '560+', label: 'Worldwide Donor', Icon: PublicOutlined },
              ].map((stat, i) => (
                <Grid item xs={6} md={3} key={i} sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Box
                    sx={{
                      width: { xs: 130, md: 160 },
                      height: { xs: 130, md: 160 },
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '1px solid rgba(255,255,255,0.45)',
                      borderRadius: '50%',
                      py: 3,
                      px: 2,
                      boxSizing: 'border-box',
                    }}
                  >
                    <stat.Icon sx={{ fontSize: 30, color: '#FFFFFF', mb: 1, display: 'block' }} />
                    <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontWeight: 800, fontSize: { xs: 26, md: 30 }, color: '#FFFFFF', lineHeight: 1, letterSpacing: '0.02em' }}>
                      {stat.value}
                    </Typography>
                    <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontWeight: 400, fontSize: { xs: 10, md: 12 }, color: '#FFFFFF', textAlign: 'center', lineHeight: 1.3, mt: 1, maxWidth: '90%', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                      {stat.label}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Container>
      </Box>

      {/* Charities Information - header, card, tabs: same width (1000px) */}
      <Box sx={{ backgroundColor: DARK_SECTION, py: { xs: 6, md: 10 }, pb: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg" sx={{ width: '100%', px: { xs: 2, sm: 3 } }}>
          <Box sx={{ maxWidth: 1000, width: '100%', mx: 'auto' }}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { md: 'center' }, justifyContent: 'space-between', gap: 2, mb: 3 }}>
              <Box sx={{ flex: { md: '1 1 auto' } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Favorite sx={{ fontSize: 18, color: BRAND_ORANGE }} />
                  <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontWeight: 600, fontSize: 12, color: BRAND_ORANGE, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                    Our Organization History
                  </Typography>
                </Box>
                <Typography component="h2" sx={{ fontFamily: '"Poppins", sans-serif', fontWeight: 700, fontSize: { xs: 20, md: 26 }, color: '#FFFFFF', lineHeight: 1.2 }}>
                  Charitics Information of
                  <br />
                  Event Schedule
                </Typography>
              </Box>
              <Button
                variant="contained"
                disableElevation
                onClick={() => navigate('/campaigns')}
                sx={{
                  flexShrink: 0,
                  minWidth: { xs: 140, md: 180 },
                  height: 48,
                  borderRadius: 9999,
                  backgroundColor: BRAND_ORANGE,
                  color: '#FFFFFF',
                  px: 2,
                  py: 1.25,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1.5,
                  '&:hover': { backgroundColor: '#d35218' },
                }}
              >
                <Box sx={{ width: 32, height: 32, borderRadius: '50%', backgroundColor: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Favorite sx={{ fontSize: 16, color: BRAND_ORANGE }} />
                </Box>
                <Typography component="span" sx={{ fontFamily: '"Poppins", sans-serif', fontWeight: 700, fontSize: 15, color: '#FFFFFF' }}>
                  Donate Now
                </Typography>
              </Button>
            </Box>

            <Box sx={{ position: 'relative' }}>
            <Box
              sx={{
                backgroundColor: '#fff',
                borderRadius: 3,
                overflow: 'hidden',
                boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
              }}
            >
              <Grid container>
                <Grid item xs={12} md={5}>
                  <Box
                    component="img"
                    src={cardContent.image}
                    alt={cardContent.tab}
                    sx={{
                      width: '100%',
                      height: { xs: 240, md: '100%' },
                      minHeight: 280,
                      objectFit: 'cover',
                      display: 'block',
                      borderTopLeftRadius: 24,
                      borderTopRightRadius: { xs: 24, md: 0 },
                      borderBottomLeftRadius: { xs: 0, md: 24 },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={7} sx={{ p: { xs: 3, md: 4 }, display: 'flex', flexDirection: 'column' }}>
                  <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontWeight: 700, fontSize: 22, color: '#1a1a1a', mb: 2 }}>
                    {cardContent.heading}
                  </Typography>
                  <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontWeight: 400, fontSize: 14, lineHeight: 1.8, color: '#4B4646', mb: 2 }}>
                    {cardContent.paragraph}
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { fontFamily: '"Poppins", sans-serif', fontSize: 14, lineHeight: 1.8, color: '#4B4646', mb: 0.5 }, listStyle: 'none' }}>
                    {cardContent.bullets.map((item, i) => (
                      <Box component="li" key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, '&::before': { content: '""', width: 6, height: 6, borderRadius: '50%', backgroundColor: BRAND_ORANGE, mt: 1.2, flexShrink: 0 } }}>
                        {item}
                      </Box>
                    ))}
                  </Box>
                  {cardContent.secondParagraph && (
                    <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontWeight: 400, fontSize: 14, lineHeight: 1.8, color: '#4B4646', mt: 2 }}>
                      {cardContent.secondParagraph}
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </Box>
            <Box
              sx={{
                position: 'absolute',
                right: -24,
                top: '50%',
                transform: 'translateY(-50%)',
                width: 80,
                height: 80,
                background: `radial-gradient(circle at 30% 30%, ${BRAND_ORANGE}40, transparent 70%)`,
                borderRadius: '50%',
                pointerEvents: 'none',
                display: { xs: 'none', md: 'block' },
              }}
            />
            </Box>

            <Box sx={{ mt: 6 }}>
              <Tabs
                value={tabValue}
                onChange={(e, v) => setTabValue(v)}
                sx={{
                  minHeight: 56,
                  borderBottom: '1px solid rgba(255,255,255,0.3)',
                  '& .MuiTab-root': {
                    color: 'rgba(255,255,255,0.95)',
                    fontFamily: '"Poppins", sans-serif',
                    fontWeight: 500,
                    fontSize: 15,
                    textTransform: 'none',
                    flex: 1,
                    minWidth: 0,
                    maxWidth: 'none',
                  },
                  '& .Mui-selected': { color: BRAND_ORANGE, fontWeight: 600 },
                  '& .MuiTabs-indicator': { backgroundColor: BRAND_ORANGE, height: 5 },
                  '& .MuiTabs-flexContainer': { display: 'flex' },
                }}
              >
                <Tab label="Our Mission" id="about-tab-0" />
                <Tab label="Our Vision" id="about-tab-1" />
                <Tab label="Our History" id="about-tab-2" />
              </Tabs>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Reviews - What People Say About us: community photo + latest 10 approved reviews */}
      <Box sx={{ backgroundColor: '#fff', py: { xs: 6, md: 10 } }}>
        <Container maxWidth="lg" sx={{ width: '100%', px: { xs: 2, sm: 3 } }}>
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <Favorite sx={{ fontSize: 16, color: BRAND_ORANGE }} />
              <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontWeight: 600, fontSize: 12, color: BRAND_ORANGE, letterSpacing: '0.03em' }}>
                Start donating your people
              </Typography>
            </Box>
            <Typography component="h2" sx={{ fontFamily: '"Poppins", sans-serif', fontWeight: 700, fontSize: { xs: 24, md: 30 }, color: '#2D2929', lineHeight: 1.2 }}>
              What People Say About us
            </Typography>
          </Box>

          <Grid container spacing={4} alignItems="stretch">
            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  position: 'relative',
                  height: '100%',
                  minHeight: { xs: 260, md: 320 },
                  borderRadius: 2,
                  overflow: 'hidden',
                  boxShadow: '0 16px 40px rgba(41,56,53,0.14)',
                }}
              >
                <Box
                  component="img"
                  src={ASSETS.cause1}
                  alt="Communities supported by Roots and Wings"
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: 'center 30%',
                    display: 'block',
                    position: 'absolute',
                    inset: 0,
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    background: 'linear-gradient(180deg, rgba(41,56,53,0.08) 35%, rgba(41,56,53,0.55) 100%)',
                  }}
                />
                <FormatQuote sx={{ position: 'absolute', top: 18, left: 18, fontSize: 42, color: '#fff' }} />
                <Typography
                  sx={{
                    position: 'absolute',
                    left: 20,
                    right: 20,
                    bottom: 20,
                    fontFamily: '"Poppins", sans-serif',
                    fontWeight: 600,
                    fontSize: 14,
                    color: '#fff',
                    letterSpacing: '0.02em',
                  }}
                >
                  Voices from the communities we serve
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={8}>
              <Box sx={{ backgroundColor: '#FAF9F6', borderRadius: 2, p: { xs: 3, md: 4 }, height: '100%', position: 'relative', minHeight: { xs: 260, md: 320 } }}>
                {reviews.length ? (
                  <>
                    <FormatQuote sx={{ fontSize: 56, color: BRAND_ORANGE, opacity: 0.9, mb: 1 }} />
                    <Box sx={{ display: 'flex', gap: 0.25, mb: 1.5 }}>
                      {[1, 2, 3, 4, 5].map((i) => (
                        <Typography key={i} sx={{ color: i <= (reviews[reviewIndex]?.rating || 0) ? BRAND_ORANGE : '#E5E0D8', fontSize: 18 }}>★</Typography>
                      ))}
                    </Box>
                    <Typography
                      component="blockquote"
                      sx={{
                        fontFamily: '"Poppins", sans-serif',
                        fontWeight: 400,
                        fontSize: 16,
                        lineHeight: 1.8,
                        color: '#4B4646',
                        fontStyle: 'italic',
                        mb: 3,
                      }}
                    >
                      {reviews[reviewIndex].text}
                    </Typography>
                    <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontWeight: 700, fontSize: 16, color: '#2D2929' }}>
                      {reviews[reviewIndex].author}
                    </Typography>
                    {reviews.length > 1 ? (
                      <Box sx={{ position: 'absolute', bottom: 24, right: 24, display: 'flex', gap: 1 }}>
                        <Button
                          size="small"
                          onClick={() => setReviewIndex((i) => (i === 0 ? reviews.length - 1 : i - 1))}
                          sx={{
                            minWidth: 44,
                            height: 44,
                            borderRadius: '50%',
                            border: '1px solid rgba(0,0,0,0.2)',
                            backgroundColor: '#fff',
                            color: '#333',
                            '&:hover': { backgroundColor: '#f5f5f5', borderColor: 'rgba(0,0,0,0.3)' },
                          }}
                        >
                          <ChevronLeft />
                        </Button>
                        <Button
                          size="small"
                          onClick={() => setReviewIndex((i) => (i === reviews.length - 1 ? 0 : i + 1))}
                          sx={{
                            minWidth: 44,
                            height: 44,
                            borderRadius: '50%',
                            border: '1px solid rgba(0,0,0,0.2)',
                            backgroundColor: '#fff',
                            color: '#333',
                            '&:hover': { backgroundColor: '#f5f5f5', borderColor: 'rgba(0,0,0,0.3)' },
                          }}
                        >
                          <ChevronRight />
                        </Button>
                      </Box>
                    ) : null}
                  </>
                ) : (
                  <Typography sx={{ fontFamily: '"Poppins", sans-serif', color: '#4B4646', py: 6 }}>
                    Approved reviews will appear here.
                  </Typography>
                )}
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Middle CTA - Children Need Your Help (5th section): sub-header, headline on 2 lines, Get A Quote button */}
      <Box
        sx={{
          position: 'relative',
          minHeight: { xs: 320, md: 400 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundImage: `url(${ASSETS.parallax})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          '&::before': { content: '""', position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)' },
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 1, textAlign: 'center', px: 2 }}>
          <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontWeight: 600, fontSize: 12, color: BRAND_ORANGE, letterSpacing: '0.03em', mb: 1 }}>
            Start Donating For People
          </Typography>
          <Typography
            sx={{
              fontFamily: '"Poppins", sans-serif',
              fontWeight: 700,
              fontSize: { xs: 26, md: 36 },
              color: '#FFFFFF',
              lineHeight: 1.2,
              mb: 4,
              textShadow: '0 2px 8px rgba(0,0,0,0.4)',
            }}
          >
            Children Need Your Help
            <br />
            By Donating Today
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Button
              variant="contained"
              disableElevation
              onClick={() => navigate('/contact')}
              sx={{
                height: 50,
                borderRadius: 9999,
                backgroundColor: BRAND_ORANGE,
                color: '#FFFFFF',
                fontFamily: '"Poppins", sans-serif',
                fontWeight: 600,
                fontSize: 16,
                px: 3,
                py: 1.5,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                '&:hover': { backgroundColor: '#d35218' },
              }}
            >
              <Box sx={{ width: 32, height: 32, borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <ArrowForward sx={{ fontSize: 18, color: '#fff' }} />
              </Box>
              Get A Quote
            </Button>
          </Box>
        </Box>
        <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, lineHeight: 0 }}>
          <Box component="img" src={ASSETS.bannerBottom} alt="" sx={{ width: '100%', height: 60, objectFit: 'cover', objectPosition: 'center', display: 'block' }} />
        </Box>
      </Box>

      {/* Join With Us - stats overlay */}
      <Box sx={{ backgroundColor: '#fff', py: { xs: 6, md: 8 } }}>
        <Container maxWidth={false} sx={{ width: '100%', px: { xs: 2, sm: 3 } }}>
          <Typography sx={{ fontFamily: '"Caveat", cursive', fontWeight: 700, fontSize: { xs: 24, md: 27 }, color: '#313530', textAlign: 'center', mb: '25px' }}>
            Join With Us
          </Typography>
          <Typography
            component="h2"
            sx={{ fontFamily: '"Nunito", sans-serif', fontWeight: 900, fontSize: { xs: 26, sm: 34, md: 40 }, lineHeight: 1.25, color: '#2D2929', textAlign: 'center', mb: { xs: '30px', md: '50px' } }}
          >
            Join in Roots & Wings
            <br />
            for Better Tomorrow
          </Typography>
          <Box
            sx={{
              position: 'relative',
              overflow: 'hidden',
              maxWidth: 1200,
              mx: 'auto',
              borderRadius: { xs: 16, md: '23px' },
              minHeight: { xs: 200, md: 238 },
              display: 'flex',
              flexDirection: 'column',
              border: '1px solid #c4a574',
              backgroundImage: `url(${ASSETS.parallax})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              '&::before': { content: '""', position: 'absolute', inset: 0, backgroundColor: 'rgba(36, 88, 78, 0.63)', borderRadius: '23px' },
            }}
          >
            <Box
              sx={{
                position: 'relative',
                zIndex: 1,
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                flexWrap: 'wrap',
                justifyContent: 'space-evenly',
                alignItems: 'center',
                flex: 1,
                minHeight: { xs: 200, md: 238 },
                gap: { xs: 3, sm: 0 },
                py: { xs: 4, md: 5 },
                px: { xs: 3, md: 4 },
              }}
            >
              <SiteStatsItems />
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default AboutPage;
